import { Alert, Platform } from 'react-native';

/**
 * CORE-1577 — одно сообщение человеку, которое доходит и на вебе.
 *
 * `Alert.alert` в react-native-web — пустая функция (`static alert() {}` в
 * `react-native-web/dist/exports/Alert`). Поэтому экран, сообщающий об исходе
 * через `Alert.alert`, на вебе не сообщает ничего: человек нажимает кнопку, и
 * ему не отвечают ни «готово», ни «не получилось».
 *
 * Для удаления данных ИИ это не мелочь, а невыполненное требование: вся задача
 * про то, чтобы неудача стирания была видна. Отсюда и эта обёртка — на вебе
 * `window.alert`, на устройстве прежний `Alert.alert`.
 *
 * Применена она пока только там, где это требование есть. В репозитории есть и
 * другие обработчики с той же болезнью (см. замечание в
 * `screens/dashboard/mediaEditActions.ts` по CORE-649) — починить их разом
 * значило бы менять поведение экранов, которых эта задача не касается.
 */
export function notifyUser(title: string, body: string): void {
    if (Platform.OS === 'web') {
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
            // Заголовок и текст одним сообщением: у window.alert нет заголовка,
            // а терять его нельзя — «Удалено не всё» несёт половину смысла.
            window.alert(`${title}\n\n${body}`);
        }
        return;
    }
    Alert.alert(title, body);
}

/**
 * CORE-1577 — подтверждение необратимого действия, которое работает и на вебе.
 *
 * `Alert.alert` с кнопками на вебе — та же пустая функция, и последствие хуже,
 * чем потерянное сообщение: обработчик подтверждения не вызывается никогда, то
 * есть кнопка на вебе не делает ничего. Найдено живым прогоном в браузере —
 * «Удалить данные ИИ-чата» в настройках мастера молчала.
 *
 * Разрешение приходит значением, а не колбэком: на вебе `window.confirm`
 * синхронный, и возвращать «да» из него через колбэк значило бы изобретать
 * асинхронность там, где её нет.
 */
export async function confirmDestructive(options: {
    title: string;
    body: string;
    confirmText: string;
    cancelText: string;
}): Promise<boolean> {
    const { title, body, confirmText, cancelText } = options;
    if (Platform.OS === 'web') {
        if (typeof window === 'undefined' || typeof window.confirm !== 'function') return false;
        return window.confirm(`${title}\n\n${body}`);
    }
    return new Promise<boolean>((resolve) => {
        Alert.alert(title, body, [
            { text: cancelText, style: 'cancel', onPress: () => resolve(false) },
            { text: confirmText, style: 'destructive', onPress: () => resolve(true) },
        ], { onDismiss: () => resolve(false) });
    });
}
